import styled from '@emotion/styled';
import React, { Suspense, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Lottie from 'react-lottie-player';

import successAnimation from 'src/public/animations/colorful_confetti.json';
import img from 'src/public/img/tada.png';

import { CoachNode } from 'src/generated/webapp_gql';
import { ProviderType } from 'src/modules/user/models/user';
import { Button } from 'src/shared/components/Button';
import { Modal } from 'src/shared/components/Modal';
import { color, font, mediaQueries } from 'src/shared/styles/variables';

import { config } from 'src/config/config';
import { DEFAULT_LATE_CANCELLATION_THRESHOLD, SessionSlot } from '../models/videoSessions';

const StyledModal = styled(Modal)<StyledModalProps>(props => ({
    '.ant-modal-content': {
        borderRadius: props.fullscreen ? 0 : 7.5,
        height: props.fullscreen ? '100vh' : 'auto',
        display: 'flex',
        flexFlow: 'column',
        justifyContent: 'center',
        alignItems: 'center',

        '.ant-modal-body': {
            padding: 0,
        },
    },
}));

const Wrapper = styled.div({
    padding: '63px 0 45px',
    display: 'flex',
    margin: '0 auto',
    flexDirection: 'column',
    alignItems: 'center',
    [mediaQueries.md]: {
        padding: '36px 0 26px',
    },
});

const SuccessAnimation = styled(Lottie)({
    width: 400,
    position: 'absolute',
});

const HeaderText = styled.h3(
    {
        color: color.ebonyClay,
        textAlign: 'center',
        marginBottom: 0,
    },
    font.black(font.size.extraLarge),
);

const DateText = styled.p(
    {
        color: color.blueBayoux,
        textAlign: 'center',
        opacity: 0.8,
        marginTop: 10,
    },
    font.heavy(font.size.large),
);

const SessionInfo = styled.p({
    color: color.blueBayoux,
    textAlign: 'center',
    marginTop: 15,
    marginBottom: 20,
    whiteSpace: 'break-spaces',
    ...font.roman(font.size.medium),
});

const Text = styled.p(
    {
        textAlign: 'center',
        color: color.steelGray,
        lineHeight: '26px',
        marginTop: 22,
    },
    font.book(font.size.medium),
);

const TextColored = styled.span(
    {
        textAlign: 'center',
        color: color.royalBlue,
    },
    font.heavy(font.size.medium),
);

const ImageWrapper = styled.div({
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    overflowY: 'hidden',
    width: '100%',
    minHeight: '200px',
    marginBottom: 40,
});

const ButtonsWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: 22,
});

interface StyledModalProps {
    fullscreen: boolean;
}

interface Props {
    visible: boolean;
    loading: boolean;
    sessionBooked: boolean;
    sessionSlot: SessionSlot;
    onboardingMode?: boolean;
    onConfirm: () => void;
    onExit: () => void;
    onClose: () => void;
    onSkip: () => void;
    onAddToCalendar: () => void;
    provider: CoachNode;
}

export const ScheduleSessionConfirmationModal: React.FC<Props> = ({
    visible,
    onConfirm,
    onExit,
    onClose,
    onSkip,
    onboardingMode = false,
    sessionSlot,
    loading,
    sessionBooked,
    onAddToCalendar,
    provider,
}) => {
    const { t } = useTranslation();
    const [eventAdded, setEventAdded] = useState(false);

    const endHour = sessionSlot.end.format('h:mma');
    const readableDate = `${sessionSlot.start.format('dddd, MMMM D, h:mma')} - ${endHour}`;
    const lateCancellationThreshold =
        (provider?.lateCancellationThreshold || DEFAULT_LATE_CANCELLATION_THRESHOLD) / 60 / 60;

    const handleAddToCalendar = () => {
        setEventAdded(true);
        onAddToCalendar();
    };

    const sessionType = provider.providerType === ProviderType.THERAPIST ? 'therapy' : 'coaching';
    const providerType = provider.providerType === ProviderType.THERAPIST ? 'therapist' : 'coach';

    return (
        <StyledModal
            visible={visible}
            footer={null}
            width={sessionBooked ? '100%' : 760}
            onCancel={onExit}
            centered
            data-testid="schedule-session-confirmation-modal"
            css={{ height: sessionBooked ? '100vh' : 'auto' }}
            fullscreen={sessionBooked}
        >
            <Wrapper css={{ maxWidth: sessionBooked ? 710 : 560 }}>
                {sessionBooked ? (
                    <>
                        <ImageWrapper>
                            <Suspense fallback={<img src={img} />}>
                                <SuccessAnimation
                                    animationData={successAnimation}
                                    speed={0.6}
                                    play
                                    loop
                                />
                                <img src={img} />
                            </Suspense>
                        </ImageWrapper>

                        <HeaderText>
                            {t(
                                `videoSessions:session_confirmation_modal.session_booked.${sessionType}`,
                            )}
                        </HeaderText>
                        <DateText>{readableDate}</DateText>
                        <SessionInfo>
                            <Trans
                                t={t}
                                i18nKey={`videoSessions:session_confirmation_modal.session_info.${providerType}`}
                                components={[
                                    // eslint-disable-next-line react/jsx-key
                                    <a href={config.iOSAppStoreURL}>iOS app</a>,
                                    // eslint-disable-next-line react/jsx-key
                                    <a href={config.AndroidGooglePlayStoreURL}>Android app.</a>,
                                ]}
                            />
                        </SessionInfo>
                        <ButtonsWrapper>
                            <Button
                                type={eventAdded ? 'light' : 'white'}
                                onClick={handleAddToCalendar}
                                noHorizontalPadding
                                width="normal"
                            >
                                {t(
                                    eventAdded
                                        ? 'videoSessions:session_confirmation_modal.saved'
                                        : 'videoSessions:session_confirmation_modal.add_to_calendar',
                                )}
                            </Button>
                            <Button
                                type="accept"
                                onClick={onClose}
                                noHorizontalPadding
                                marginLeft={18}
                                width="normal"
                            >
                                {t('videoSessions:session_confirmation_modal.book_another_session')}
                            </Button>
                        </ButtonsWrapper>
                        {onboardingMode ? (
                            <Button marginTop={15} type="noBackground" onClick={onSkip}>
                                {t('common.skip')}
                            </Button>
                        ) : (
                            <Button onClick={onExit} type="noBackground" marginTop={15}>
                                {/* TODO: Update this copy to "Continue" when showIntakeForm is turned on */}
                                {t('videoSessions:session_confirmation_modal.back_to_dashboard')}
                            </Button>
                        )}
                    </>
                ) : (
                    <>
                        <HeaderText>{readableDate}</HeaderText>
                        <Text>
                            <Trans
                                t={t}
                                i18nKey="videoSessions:session_confirmation_modal.description"
                                values={{ lateCancellationThreshold }}
                                components={[
                                    // eslint-disable-next-line react/jsx-key
                                    <TextColored>{lateCancellationThreshold} hours</TextColored>,
                                ]}
                            />
                        </Text>
                        <ButtonsWrapper>
                            <Button
                                onClick={onClose}
                                type="white"
                                noHorizontalPadding
                                width="normal"
                            >
                                {t('videoSessions:session_confirmation_modal.back_to_calendar')}
                            </Button>
                            <Button
                                onClick={onConfirm}
                                loading={loading}
                                noHorizontalPadding
                                marginLeft={18}
                                width="normal"
                            >
                                {t('videoSessions:session_confirmation_modal.confirm')}
                            </Button>
                        </ButtonsWrapper>
                    </>
                )}
            </Wrapper>
        </StyledModal>
    );
};
